/**
 * Cutting a line of English into the pieces a child can tap.
 *
 * [Tap-to-hint](../../../docs/REVIEW.md#tap-to-hint) is *"tap any English word,
 * anywhere, to hear it and see what it means"*, and it matters more here than in
 * most apps because the whole interface is English
 * ([D-068](../../../docs/DECISIONS.md#d-068)): a child who does not know a word
 * in an **instruction** is otherwise simply stuck, with no way to ask.
 *
 * The splitting is here rather than in a component because it is a rule about
 * language, it has to behave the same wherever a word is shown, and it is full
 * of small decisions that are easy to get wrong and easy to prove:
 *
 * - `don't` and `it's` are **one** word. Splitting on every apostrophe offers a
 *   child `don` and `t`, neither of which means anything.
 * - `well-known` is one word for the same reason.
 * - Punctuation is never part of the word, so tapping `hello!` looks up `hello`.
 * - The spacing between words is kept, so the line can be rebuilt exactly. A
 *   rendered sentence must read identically whether or not its words are
 *   tappable.
 */
/**
 * What counts as a word.
 *
 * Letters, plus an apostrophe or hyphen **between** letters. The `(?<=…)` and
 * `(?=…)` are what make `don't` one word while leaving a quoted `'hello'`
 * unquoted — the apostrophe only joins when there is a letter on both sides.
 *
 * `\p{L}` rather than `a-z`: the interface is English, but a `meaning_choose`
 * option is a Danish word ([D-068](../../../docs/DECISIONS.md#d-068)) and `æ` is
 * a letter. Refusing to cut it correctly would be a bug even though nothing
 * looks the word up.
 */
const WORD = /\p{L}+(?:(?<=\p{L})['’-](?=\p{L})\p{L}+)*/gu;
/**
 * A line, cut into words and the gaps between them.
 *
 * Total: an empty line gives an empty list, and joining the pieces of any line
 * gives that line back. Both are asserted in the tests, because a renderer built
 * on this would silently drop text if either stopped being true.
 */
export function pieces(line) {
    const out = [];
    let at = 0;
    for (const match of line.matchAll(WORD)) {
        if (match.index > at)
            out.push({ text: line.slice(at, match.index), lookup: null });
        out.push({ text: match[0], lookup: lookupForm(match[0]) });
        at = match.index + match[0].length;
    }
    if (at < line.length)
        out.push({ text: line.slice(at), lookup: null });
    return out;
}
/**
 * The form a word is looked up by.
 *
 * Lower-cased, and the typographic apostrophe folded to the plain one so
 * `don’t` and `don't` are the same lookup. A course is authored by hand in a
 * text editor and will contain both.
 *
 * Deliberately **not** `normalise` from [`scoring`](./scoring.ts), which folds
 * `æ ø å` to `ae oe aa` so a child's typo is forgiven. That is a different job:
 * this one has to produce a string the database can match a stored word by, and
 * folding letters it does not fold would make it miss.
 */
export const lookupForm = (word) => word.toLowerCase().replace(/’/g, "'");
/**
 * Every distinct word in a line, in the order they first appear.
 *
 * For asking the server about a whole sentence at once rather than a word at a
 * time: a child taps one word, but the screen already knows every word it is
 * showing, and one request beats a dozen.
 */
export function wordsIn(line) {
    const seen = new Set();
    for (const piece of pieces(line)) {
        if (piece.lookup !== null)
            seen.add(piece.lookup);
    }
    return [...seen];
}
//# sourceMappingURL=words.js.map